import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Bot, PhoneCall, Megaphone, Wand2, Rocket, Check } from "lucide-react";

interface PlatformPackage {
  id: number;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  features: string[];
  includedModules: string[];
  setupPriceCents: number;
  monthlyPriceCents: number;
  yearlyPriceCents: number | null;
  billingInterval: string;
  currency: string;
}

const PACKAGE_ICONS: Record<string, typeof PhoneCall> = {
  "ai-receptionist-starter": PhoneCall,
  "sales-rep-pro": Bot,
  "marketing-autopilot": Megaphone,
  "content-factory": Wand2,
  "full-ai-os": Rocket,
};

function fmtUSD(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function PlatformServices() {
  const [, setLocation] = useLocation();
  const [selected, setSelected] = useState<PlatformPackage | null>(null);
  const [form, setForm] = useState({
    businessName: "",
    website: "",
    industry: "",
    ownerEmail: "",
    ownerPhone: "",
    ownerName: "",
  });

  const packages = useQuery({
    queryKey: ["platform-catalog", "packages"],
    queryFn: async () => {
      const res = await fetch("/api/platform-catalog/packages");
      if (!res.ok) throw new Error("Failed to load packages");
      return res.json() as Promise<{ packages: PlatformPackage[] }>;
    },
  });

  const checkout = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error("No package selected");
      const origin = window.location.origin;
      const res = await fetch("/api/platform-catalog/orders", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageSlug: selected.slug,
          businessName: form.businessName,
          website: form.website || undefined,
          industry: form.industry || undefined,
          ownerEmail: form.ownerEmail,
          ownerPhone: form.ownerPhone || undefined,
          ownerName: form.ownerName || undefined,
          successUrl: `${origin}/platform/order/{ORDER_ID}?session={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/platform-services?cancelled=1`,
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ order: any; checkoutUrl: string }>;
    },
    onSuccess: (data) => {
      // Redirect to Stripe Checkout. Cleanest UX is full-page nav.
      window.location.href = data.checkoutUrl;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  useEffect(() => {
    if (new URLSearchParams(window.location.search).has("cancelled")) {
      toast.info("Checkout cancelled — your details are still saved");
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-semibold tracking-tight">AR Dynamics</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm text-muted-foreground">
            <Link href="/" className="hover:text-foreground">Home</Link>
            <Link href="/build" className="hover:text-foreground">Custom Build</Link>
            <Link href="/portal" className="hover:text-foreground">Portal</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-12 text-center">
        <Badge variant="outline" className="mb-4">AR Dynamics OS</Badge>
        <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight">An AI workforce for your business.</h1>
        <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
          Pick a package. Tell us your business. We deploy AI employees — receptionist, sales rep, marketing — that
          start working in your backend within minutes.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-16">
        {packages.isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-64 w-full" />)}
          </div>
        ) : packages.data?.packages.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.data.packages.map((p) => {
              const Icon = PACKAGE_ICONS[p.slug] ?? Sparkles;
              return (
                <Card key={p.id} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="rounded-lg bg-primary/10 p-2.5">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      {p.slug === "full-ai-os" && <Badge>Most popular</Badge>}
                    </div>
                    <CardTitle className="mt-3">{p.name}</CardTitle>
                    <CardDescription>{p.tagline}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col gap-4">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-semibold">{fmtUSD(p.monthlyPriceCents)}</span>
                      <span className="text-sm text-muted-foreground">/month</span>
                    </div>
                    {p.setupPriceCents > 0 && (
                      <p className="text-xs text-muted-foreground">+ {fmtUSD(p.setupPriceCents)} one-time setup</p>
                    )}
                    <ul className="space-y-1.5 text-sm">
                      {p.features.slice(0, 6).map((f) => (
                        <li key={f} className="flex items-start gap-2">
                          <Check className="h-3.5 w-3.5 mt-1 text-primary shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Button className="mt-auto" onClick={() => setSelected(p)} data-testid={`button-buy-${p.slug}`}>
                      Get started
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <p className="text-center text-muted-foreground">No packages available right now.</p>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(v: boolean) => { if (!v) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selected?.name ?? "Get started"}</DialogTitle>
            <DialogDescription>
              Tell us about your business. We'll set up your AI workforce immediately after payment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Field label="Business name *" value={form.businessName} onChange={(v) => setForm({ ...form, businessName: v })} placeholder="Acme Roofing" />
            <Field label="Your website (we'll crawl it to build your AI)" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://acmeroofing.com" />
            <Field label="Industry" value={form.industry} onChange={(v) => setForm({ ...form, industry: v })} placeholder="roofing, dental, real_estate…" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Your name" value={form.ownerName} onChange={(v) => setForm({ ...form, ownerName: v })} />
              <Field label="Email *" value={form.ownerEmail} onChange={(v) => setForm({ ...form, ownerEmail: v })} type="email" />
            </div>
            <Field label="Phone (for the AI to send you notifications)" value={form.ownerPhone} onChange={(v) => setForm({ ...form, ownerPhone: v })} type="tel" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)}>Cancel</Button>
            <Button
              onClick={() => checkout.mutate()}
              disabled={!form.businessName || !form.ownerEmail || checkout.isPending}
              data-testid="button-checkout"
            >
              {checkout.isPending ? "Redirecting…" : "Continue to checkout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs uppercase text-muted-foreground tracking-wider">{label}</label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} type={type} placeholder={placeholder} />
    </div>
  );
}
